import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROUTES } from '../config/constants';
import CustomerLayout from '../layouts/CustomerLayout';
import AdminLayout from '../layouts/AdminLayout';

// Customer pages
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage';
import ActivatePage from '../pages/ActivatePage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import DashboardPage from '../pages/DashboardPage';
import ProductsPage from '../pages/ProductsPage';
import CartPage from '../pages/CartPage';
import OrdersPage from '../pages/OrdersPage';
import OrderDetailPage from '../pages/OrderDetailPage';
import InvoicesPage from '../pages/InvoicesPage';
import InvoiceDetailPage from '../pages/InvoiceDetailPage';
import ProfilePage from '../pages/ProfilePage';
import NotificationsPage from '../pages/NotificationsPage';
import { PaymentSuccessPage, PaymentPendingPage, PaymentErrorPage } from '../pages/PaymentStatusPage';

// Admin pages
import AdminDashboardPage from '../pages/admin/AdminDashboardPage';
import AdminCustomersPage from '../pages/admin/AdminCustomersPage';
import AdminOrdersPage from '../pages/admin/AdminOrdersPage';
import AdminProductsPage from '../pages/admin/AdminProductsPage';
import AdminUsersPage from '../pages/admin/AdminUsersPage';
import AdminSettingsPage from '../pages/admin/AdminSettingsPage';

// Backoffice pages
import BackofficeLoginPage from '../pages/backoffice/BackofficeLoginPage';
import BackofficeDashboardPage from '../pages/backoffice/BackofficeDashboardPage';
import SignupRequestsPage from '../pages/backoffice/SignupRequestsPage';
import CustomerManagementPage from '../pages/backoffice/CustomerManagementPage';
import OrderManagementPage from '../pages/backoffice/OrderManagementPage';
import UserManagementPage from '../pages/backoffice/UserManagementPage';
import EpicorConnectPage from '../pages/backoffice/EpicorConnectPage';

// Notification Management pages
import NotificationTemplateManagementPage from '../pages/backoffice/NotificationTemplateManagementPageNew';
import NotificationSettingsPage from '../pages/backoffice/NotificationSettingsPageNew';
import NotificationManagementPage from '../pages/backoffice/NotificationManagementPageNew';
import ProductInfoManagementPage from '../pages/backoffice/ProductInfoManagementPage';
import PromotionManagementPage from '../pages/backoffice/PromotionManagementPage';
import SystemConfigPage from '../pages/backoffice/SystemConfigPage';

import ProtectedRoute from '../components/auth/ProtectedRoute';
import AdminRoute from '../components/auth/AdminRoute';
import BackofficeRoute from '../components/auth/BackofficeRoute';
import BackofficeLayout from '../layouts/BackofficeLayout';

export const router = createBrowserRouter([
  {
    path: ROUTES.HOME,
    element: <Navigate to={ROUTES.DASHBOARD} replace />,
  },
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
  },
  {
    path: ROUTES.REGISTER,
    element: <RegisterPage />,
  },
  {
    path: ROUTES.ACTIVATE,
    element: <ActivatePage />,
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    element: <ForgotPasswordPage />,
  },
  {
    path: ROUTES.RESET_PASSWORD,
    element: <ResetPasswordPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <CustomerLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: ROUTES.DASHBOARD,
        element: <DashboardPage />,
      },
      {
        path: ROUTES.PRODUCTS,
        element: <ProductsPage />,
      },
      {
        path: ROUTES.CART,
        element: <CartPage />,
      },
      {
        path: ROUTES.ORDERS,
        element: <OrdersPage />,
      },
      {
        path: ROUTES.ORDER_DETAIL,
        element: <OrderDetailPage />,
      },
      {
        path: ROUTES.INVOICES,
        element: <InvoicesPage />,
      },
      {
        path: ROUTES.INVOICE_DETAIL,
        element: <InvoiceDetailPage />,
      },
      {
        path: ROUTES.PROFILE,
        element: <ProfilePage />,
      },
      {
        path: ROUTES.NOTIFICATIONS,
        element: <NotificationsPage />,
      },
      {
        path: ROUTES.PAYMENT_SUCCESS,
        element: <PaymentSuccessPage />,
      },
      {
        path: ROUTES.PAYMENT_PENDING,
        element: <PaymentPendingPage />,
      },
      {
        path: ROUTES.PAYMENT_ERROR,
        element: <PaymentErrorPage />,
      },
    ],
  },
  {
    path: ROUTES.ADMIN,
    element: (
      <AdminRoute>
        <AdminLayout />
      </AdminRoute>
    ),
    children: [
      {
        path: ROUTES.ADMIN,
        element: <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />,
      },
      {
        path: ROUTES.ADMIN_DASHBOARD,
        element: <AdminDashboardPage />,
      },
      {
        path: ROUTES.ADMIN_CUSTOMERS,
        element: <AdminCustomersPage />,
      },
      {
        path: ROUTES.ADMIN_ORDERS,
        element: <AdminOrdersPage />,
      },
      {
        path: ROUTES.ADMIN_PRODUCTS,
        element: <AdminProductsPage />,
      },
      {
        path: ROUTES.ADMIN_USERS,
        element: <AdminUsersPage />,
      },
      {
        path: ROUTES.ADMIN_SETTINGS,
        element: <AdminSettingsPage />,
      },
    ],
  },
  {
    path: ROUTES.BACKOFFICE_LOGIN,
    element: <BackofficeLoginPage />,
  },
  {
    path: ROUTES.BACKOFFICE,
    element: (
      <BackofficeRoute>
        <BackofficeLayout />
      </BackofficeRoute>
    ),
    children: [
      {
        path: ROUTES.BACKOFFICE,
        element: <Navigate to={ROUTES.BACKOFFICE_DASHBOARD} replace />,
      },
      {
        path: ROUTES.BACKOFFICE_DASHBOARD,
        element: <BackofficeDashboardPage />,
      },
      {
        path: ROUTES.BACKOFFICE_SIGNUP_REQUESTS,
        element: <SignupRequestsPage />,
      },
      {
        path: ROUTES.BACKOFFICE_CUSTOMER_MANAGEMENT,
        element: <CustomerManagementPage />,
      },
      {
        path: ROUTES.BACKOFFICE_ORDER_MANAGEMENT,
        element: <OrderManagementPage />,
      },
      {
        path: ROUTES.BACKOFFICE_USER_MANAGEMENT,
        element: <UserManagementPage />,
      },
      {
        path: ROUTES.BACKOFFICE_EPICOR_CONNECT,
        element: <EpicorConnectPage />,
      },
      {
        path: ROUTES.BACKOFFICE_NOTIFICATION_TEMPLATES,
        element: <NotificationTemplateManagementPage />,
      },
      {
        path: ROUTES.BACKOFFICE_NOTIFICATION_SETTINGS,
        element: <NotificationSettingsPage />,
      },
      {
        path: ROUTES.BACKOFFICE_NOTIFICATION_MANAGEMENT,
        element: <NotificationManagementPage />,
      },
      {
        path: ROUTES.BACKOFFICE_PRODUCT_INFO,
        element: <ProductInfoManagementPage />,
      },
      {
        path: ROUTES.BACKOFFICE_PROMOTIONS,
        element: <PromotionManagementPage />,
      },
      {
        path: 'system-config',
        element: <SystemConfigPage />,
      },
    ],
  },
]);

export default router;